
import { Component, Prop, Vue } from 'vue-property-decorator';
import { DateTime } from 'luxon';
import { cloneDeep } from 'lodash';
import { getImageURL } from '@/utils/getters';
import { formatAddress } from '@/utils/format';
import { decodeAnsweredCustomQuestions } from '@/utils/decoding';
import { BeoExportData } from '@/utils/beo-export';
import BeoOrderSummary from '@/components/catering-beo/BeoOrderSummary.vue';

@Component<BeoReport>({
	components: {
		BeoOrderSummary
	}
})
export default class BeoReport extends Vue {
	@Prop({ type: Object, required: true }) private order!: Order;
	@Prop({ type: Object, required: true }) private restaurant!: Restaurant;
	@Prop({ type: Object, required: true }) private reportSections!: Record<string, boolean | Record<string, boolean>>;
	@Prop({ type: String, default: null }) private formattedEventDate!: string | null;

	private get event(): EventInfo | null {
		return this.order?.data.checkout.cateringEvent || null;
	}

	private get deliveryString(): string | null {
		const delivery = this.order.data.checkout.delivery;
		if (!delivery) {
			return this.$t('orders.details.pickup');
		}
		return formatAddress(delivery.address, delivery.extra);
	}

	private get imageURL(): string {
		return this.restaurant.image ? getImageURL(this.restaurant.image) : '';
	}

	private get orderTaxInfo(): { name: string, percentageValue: string, amount: number }[] | null {
		if (!this.reportSections.prices) {
			return null;
		}
		const taxInfo: OrderTaxInfo[] = (this.order?.data.costs as Costs & { taxInfo?: OrderTaxInfo[] })?.taxInfo || [];
		return (this.restaurant.preset_tax_rates || [])
			.filter((tax: PresetTaxes) => tax.is_default)
			.map((tax: PresetTaxes) => {
				const match = taxInfo.find((t: OrderTaxInfo) => t.name === tax.name);
				return {
					name: tax.name,
					percentageValue: tax.value,
					amount: match ? match.value : 0
				};
			});
	}

	/**
	 * Group custom questions timepicker, internal and external
	 *
	 * @return {{ timepicker: AnsweredCustomQuestion[], external: AnsweredCustomQuestion[], internal: AnsweredCustomQuestion[] }}
	 */
	private get groupedQuestions(): { timepicker: AnsweredCustomQuestion[], external: AnsweredCustomQuestion[], internal: AnsweredCustomQuestion[] } {
		const decodedQuestions = decodeAnsweredCustomQuestions(this.order?.data.checkout.questions || []);
		const timepicker: AnsweredCustomQuestion[] = [];
		const external: AnsweredCustomQuestion[] = [];
		const internal: AnsweredCustomQuestion[] = [];

		for (const q of decodedQuestions) {
			if (q.question_type === 'timepicker') {
				timepicker.push(q);
			}
			else if (q.is_internal) {
				internal.push(q);
			}
			else {
				external.push(q);
			}
		}

		return { timepicker, external, internal };
	}

	/**
	 * Format timeline events from event start/end time and timepicker question depending on which sections are displayed
	 *
	 * @return {{ time: string, millis: number, value: string }[]}
	 */
	private get eventTimeline(): { time: string, millis: number, value: string }[] {
		const timeEvents: { time: string, millis: number, value: string }[] = [];
		const timeline: Record<string, boolean> = this.reportSections.timeline as Record<string, boolean>;
		const timepickerFormat = this.$t('orders.guest_details.takeout.timepicker_format');
		if (this.event && timeline.eventStartEnd && this.event.eventStartDate && this.event.eventEndDate) {
			const startDt = this.normalizeEventTime(this.event.eventStartDate);
			const endDt = this.normalizeEventTime(this.event.eventEndDate);
			if (startDt.isValid && endDt.isValid) {
				timeEvents.push(
					{ time: startDt.toFormat(timepickerFormat), millis: startDt.toMillis(), value: this.$t('orders.beo.event_start_time') },
					{ time: endDt.toFormat(timepickerFormat), millis: endDt.toMillis(), value: this.$t('orders.beo.event_end_time') }
				);
			}
		}
		else if (!this.event && timeline.eventStartEnd && this.order.pickup_time) {
			const pickupTime = DateTime.fromISO(this.order.pickup_time);
			const timelineString = this.order.data.checkout.delivery ? this.$t('orders.beo.delivery_time') : this.$t('orders.beo.pickup_time');
			timeEvents.push({ time: pickupTime.toFormat(timepickerFormat), millis: pickupTime.toMillis(), value: timelineString });
		}
		if (this.groupedQuestions.timepicker.length) {
			const questions = this.groupedQuestions.timepicker
				.filter((q: AnsweredCustomQuestion) => (q.is_internal && timeline.timepickerInternal) || (!q.is_internal && timeline.timepickerExternal))
				.map((q: AnsweredCustomQuestion) => {
					const dt = DateTime.fromISO(q.answer);
					return { time: dt.toFormat(timepickerFormat), millis: dt.toMillis(), value: this.$options.filters?.localize(q, 'question') || q.question };
				});
			timeEvents.push(...questions);
		}
		return timeEvents.sort((a, b) => a.millis - b.millis);
	}

	/**
	 * Merge duplicate items for when item details aren't displayed
	 *
	 * @return {OrderMenuGroup[]}
	 */
	private get mergedItems(): OrderMenuGroup[] {
		if (!this.order || this.reportSections.itemDetails) {
			return [];
		}

		const menus = cloneDeep(this.order.data.items as unknown as OrderMenuGroup[]);
		menus.forEach((menu: OrderMenuGroup) => {
			menu.sections?.forEach((section: OrderItemGroup) => {
				if (section.items?.length) {
					section.items = this.mergeItemsById(section.items);
				}
			});
		});
		return menus;
	}

	/**
	 * Merge items with the same id and sum their quantities
	 *
	 * @param {OrderItem[]} items
	 * @return {OrderItem[]}
	 */
	private mergeItemsById(items: OrderItem[]): OrderItem[] {
		const merged = new Map<number, OrderItem>();
		items.forEach(item => {
			const existing = merged.get(item.id);
			if (existing) {
				existing.quantity += item.quantity;
				existing.orderPrice = (existing.orderPrice ?? 0) + (item.orderPrice ?? 0);
			}
			else {
				merged.set(item.id, { ...item });
			}
		});
		return Array.from(merged.values());
	}

	/**
	 * Normalize an event time to the order's pickup date
	 * This is to ensure that event start and end times correctly line up with the timepicker questions
	 *
	 * @param {string} time
	 * @return {DateTime}
	 */
	private normalizeEventTime(time: string): DateTime {
		const parsedTime = DateTime.fromISO(time);
		return DateTime.fromISO(this.order.pickup_time).set({ hour: parsedTime.hour, minute: parsedTime.minute });
	}

	/**
	 * Collect the data needed for the DOCX export
	 *
	 * @return {BeoExportData}
	 */
	public getExportData(): BeoExportData {
		return {
			order: this.order,
			restaurant: this.restaurant,
			reportSections: this.reportSections,
			eventTimeline: this.eventTimeline,
			groupedQuestions: this.groupedQuestions,
			orderTaxInfo: this.orderTaxInfo,
			mergedItems: this.mergedItems,
			formattedEventDate: this.formattedEventDate,
			imageURL: this.imageURL
		};
	}
}
